import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import clinicAPI from "../../../services/clinicApi";
import { formatDateRu, formatTimeRu } from "../../../utils/appointmentLabels";
import { useHubBasePath } from "../../../utils/hubPaths";
import {
  HubListGroup,
  HubRow,
  HubScreen,
  HubSegment,
  HubSheet,
} from "../AdminPortal/HubPwa";
import TrainerGroupSchedule from "./TrainerGroupSchedule";
import TrainerProfileHero from "./TrainerProfileHero";
import { resolveTrainerPwaTab, TRAINER_PWA_TABS } from "./doctorConstants";
import aa from "../AdminPortal/adminApp.module.css";
import { EmptyState } from "../../ui";

const TrainerCalendarPwa = lazy(() => import("./TrainerCalendarPwa"));
const TrainerAssignmentsPwa = lazy(() => import("./TrainerAssignmentsPwa"));
const TrainerSettingsPwa = lazy(() => import("./TrainerSettingsPwa"));

function TabLoading() {
  return <p className={aa.hubHint}>Загрузка…</p>;
}

function sessionCounts(sessions = []) {
  const now = new Date();
  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(now);
  endOfDay.setHours(23, 59, 59, 999);
  const weekStart = new Date(now);
  const day = weekStart.getDay();
  weekStart.setDate(weekStart.getDate() + (day === 0 ? -6 : 1 - day));
  weekStart.setHours(0, 0, 0, 0);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);

  const active = sessions.filter((row) => row.status !== "cancelled");
  const today = active.filter((row) => {
    const t = new Date(row.start_datetime);
    return t >= startOfDay && t <= endOfDay;
  }).length;
  const week = active.filter((row) => {
    const t = new Date(row.start_datetime);
    return t >= weekStart && t <= weekEnd;
  }).length;
  const upcoming = active
    .filter((row) => new Date(row.start_datetime).getTime() >= now.getTime())
    .sort(
      (a, b) =>
        new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime(),
    );

  return { today, week, upcoming };
}

function sessionTitle(session) {
  if (session.title?.trim()) return session.title;
  if (session.group?.name) return session.group.name;
  if (session.specialization?.name) return session.specialization.name;
  return session.session_kind === "event" ? "Событие" : "Занятие";
}

export default function TrainerProfilePwa({
  doctorId,
  doctor,
  edit,
  setEdit,
  branches,
  groupCatalog,
  workload,
  summary,
  loading,
  onSaveDoctor,
  onSaveBranches,
  onReload,
}) {
  const navigate = useNavigate();
  const hubBase = useHubBasePath();
  const currentUserId = useSelector((state) => state.auth.profile?.id);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const tab = resolveTrainerPwaTab(tabFromUrl);

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [assignmentsSaving, setAssignmentsSaving] = useState(false);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);

  const canMessage =
    doctor?.user_id && String(doctor.user_id) !== String(currentUserId);

  const openChat = () => {
    if (!doctor?.user_id) return;
    setActionsOpen(false);
    navigate(`${hubBase}/chat/member/${doctor.user_id}`, {
      state: { name: doctor.full_name || undefined },
    });
  };

  const setTab = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === "overview") next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!doctorId) return undefined;
    let cancelled = false;
    setSessionsLoading(true);
    const from = new Date();
    const to = new Date(from);
    to.setDate(to.getDate() + 60);
    clinicAPI
      .getTrainingSessions({
        doctor_id: doctorId,
        from: from.toISOString().slice(0, 10),
        to: to.toISOString().slice(0, 10),
      })
      .then((res) => {
        if (!cancelled) setSessions(res.sessions || []);
      })
      .catch(() => {
        if (!cancelled) setSessions([]);
      })
      .finally(() => {
        if (!cancelled) setSessionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [doctorId]);

  const { today, week, upcoming } = useMemo(() => sessionCounts(sessions), [sessions]);

  const openClubCalendar = () => {
    navigate(`/manage/app/schedule?trainer=${doctorId}&view=week`);
  };

  const openSession = (session) => {
    const day = session.start_datetime?.slice(0, 10);
    navigate(
      `/manage/app/schedule?trainer=${doctorId}&view=day${day ? `&date=${day}` : ""}`,
    );
  };

  const monthLabel = workload?.month
    ? new Date(`${workload.month}-01`).toLocaleDateString("ru-RU", {
        month: "short",
        year: "numeric",
      })
    : "Месяц";

  const kpiItems = [
    { key: "today", label: "Сегодня", value: String(today), hint: "занятий" },
    { key: "week", label: "Эта неделя", value: String(week), hint: "занятий" },
    {
      key: "month",
      label: monthLabel,
      value: workload
        ? String(workload.sessions_count ?? workload.appointments_count ?? 0)
        : "—",
      hint: "занятий",
    },
    {
      key: "hours",
      label: "Нагрузка",
      value: workload ? String(workload.total_hours ?? 0) : "—",
      hint: "часов за месяц",
    },
  ];

  const overviewContent = (
    <>
      <div className={aa.trainerKpiGrid}>
        {kpiItems.map((item) => (
          <article key={item.key} className={aa.trainerKpiCard}>
            <span className={aa.trainerKpiValue}>{item.value}</span>
            <span className={aa.trainerKpiLabel}>{item.label}</span>
            <span className={aa.trainerKpiHint}>{item.hint}</span>
          </article>
        ))}
      </div>

      {upcoming[0] ? (
        <button
          type="button"
          className={aa.trainerNextSession}
          onClick={() => openSession(upcoming[0])}
        >
          <span className={aa.trainerNextSessionLabel}>Ближайшее занятие</span>
          <strong className={aa.trainerNextSessionTitle}>
            {sessionTitle(upcoming[0])}
          </strong>
          <span className={aa.trainerNextSessionMeta}>
            {formatDateRu(upcoming[0].start_datetime)} ·{" "}
            {formatTimeRu(upcoming[0].start_datetime)}
          </span>
        </button>
      ) : !sessionsLoading ? (
        <p className={aa.trainerEmptyHint}>Предстоящих занятий нет</p>
      ) : null}

      <section className={aa.trainerSection}>
        <h2 className={aa.hubSectionLabel}>Ближайшие занятия</h2>
        {sessionsLoading ? (
          <p className={aa.hubHint}>Загрузка…</p>
        ) : upcoming.length === 0 ? (
          <EmptyState title="Занятий нет" description="Создайте тренировку в календаре клуба" />
        ) : (
          <HubListGroup>
            {upcoming.slice(0, 8).map((session) => (
              <HubRow
                key={session.id}
                title={sessionTitle(session)}
                meta={formatTimeRu(session.start_datetime)}
                sub={formatDateRu(session.start_datetime)}
                badge={
                  session.session_kind === "event" ? "Событие" : undefined
                }
                badgeTone={session.session_kind === "event" ? "warn" : "accent"}
                onClick={() => openSession(session)}
              />
            ))}
          </HubListGroup>
        )}
        {upcoming.length > 8 ? (
          <button
            type="button"
            className={aa.hubSecondaryCta}
            onClick={openClubCalendar}
          >
            Все занятия в календаре
          </button>
        ) : null}
      </section>
    </>
  );

  return (
    <HubScreen
      title={doctor?.full_name || "Тренер"}
      onBack={() => navigate("/manage/app/team")}
      bodyClassName={aa.trainerProfileHubBody}
    >
      {loading && !doctor ? (
        <p className={aa.hubHint}>Загрузка…</p>
      ) : (
        <>
          <TrainerProfileHero
            doctor={doctor}
            onOpenCalendar={openClubCalendar}
            onOpenSchedule={() => setTab("schedule")}
            onOpenActions={() => setActionsOpen(true)}
          />

          <HubSheet
            open={actionsOpen}
            onClose={() => setActionsOpen(false)}
            title="Действия"
            subtitle={doctor?.full_name || undefined}
          >
            <div className={aa.trainerActionsSheet}>
              <button
                type="button"
                className={aa.hubSecondaryCta}
                onClick={() => {
                  setActionsOpen(false);
                  setTab("settings");
                }}
              >
                Редактировать аккаунт
              </button>
              <button
                type="button"
                className={aa.hubSecondaryCta}
                onClick={() => {
                  setActionsOpen(false);
                  setTab("assignments");
                }}
              >
                Назначения
              </button>
              {canMessage ? (
                <button
                  type="button"
                  className={aa.hubSecondaryCta}
                  onClick={openChat}
                >
                  Написать в чат
                </button>
              ) : null}
              {doctor?.email ? (
                <a
                  href={`mailto:${doctor.email}`}
                  className={aa.trainerActionsSheetLink}
                  onClick={() => setActionsOpen(false)}
                >
                  Написать на email
                </a>
              ) : null}
              {doctor?.user_id ? (
                <button
                  type="button"
                  className={aa.hubSecondaryCta}
                  onClick={() => {
                    setActionsOpen(false);
                    navigate(`/manage/app/team/members/${doctor.user_id}`);
                  }}
                >
                  Доступ в Команде
                </button>
              ) : null}
            </div>
          </HubSheet>

          <div className={aa.hubSegmentWrap}>
            <HubSegment tabs={TRAINER_PWA_TABS} active={tab} onChange={setTab} />
          </div>

          {tab === "overview" ? overviewContent : null}

          {tab === "calendar" ? (
            <Suspense fallback={<TabLoading />}>
              <TrainerCalendarPwa
                key={calendarRefreshKey}
                doctorId={doctorId}
                doctorBranchOptions={branches}
              />
            </Suspense>
          ) : null}

          {tab === "schedule" ? (
            <TrainerGroupSchedule
              doctorId={doctorId}
              trainerName={doctor?.full_name}
              assignedGroups={doctor?.groups || []}
              groupCatalog={groupCatalog}
              onScheduleSaved={() => {
                onReload?.();
                setCalendarRefreshKey((k) => k + 1);
              }}
              isPwa
              onGoToAssignments={() => setTab("assignments")}
            />
          ) : null}

          {tab === "assignments" ? (
            <Suspense fallback={<TabLoading />}>
              <TrainerAssignmentsPwa
                groups={groupCatalog}
                groupIds={edit.groupIds}
                onChangeAssignments={(patch) =>
                  setEdit((prev) => ({ ...prev, ...patch }))
                }
                onSave={async () => {
                  setAssignmentsSaving(true);
                  try {
                    await onSaveBranches();
                  } finally {
                    setAssignmentsSaving(false);
                  }
                }}
                saving={assignmentsSaving}
              />
            </Suspense>
          ) : null}

          {tab === "settings" ? (
            <Suspense fallback={<TabLoading />}>
              <TrainerSettingsPwa
                doctor={doctor}
                edit={edit}
                setEdit={setEdit}
                onSaveDoctor={onSaveDoctor}
              />
            </Suspense>
          ) : null}
        </>
      )}
    </HubScreen>
  );
}
